import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import StripePaymentForm from '../../components/StripePaymentForm';
import { Shield, CheckCircle2, AlertTriangle, ArrowLeft, FileText, Percent } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../../services/apiService';

// Use a consistent Quote interface
interface Quote {
  _id: string;
  reference: string;
  title: string;
  subtotal: number;
  taxAmount: number;
  total: number;
  createdAt: string;
  validUntil: string;
}

const StripePayment: React.FC = () => {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [paymentType, setPaymentType] = useState<'partial' | 'full'>('partial');
  const [isLoading, setIsLoading] = useState(true);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  // Fetch Quote Data
  useEffect(() => {
    if (!quoteId) {
      toast.error('ID de devis manquant dans l\'URL.');
      navigate('/client/quotes');
      return;
    }

    const fetchQuoteDetails = async () => {
      setIsLoading(true);
      try {
        const response = await apiService.quotes.getById(quoteId);
        // Correctly access nested data
        const quoteData = response.data?.data?.quote;
        if (quoteData) {
          setQuote(quoteData);
        } else {
          throw new Error('Format de réponse de devis invalide.');
        }
      } catch (error) {
        toast.error('Erreur lors du chargement des détails du devis.');
        navigate('/client/quotes');
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuoteDetails();
  }, [quoteId, navigate]);

  // Memoize payment amounts to prevent recalculations
  const { partialAmount, fullAmount } = useMemo(() => {
    if (!quote) return { partialAmount: 0, fullAmount: 0 };
    return {
      partialAmount: (quote.subtotal || 0) * 0.5, // 50% of subtotal
      fullAmount: quote.total || 0, // Full total amount
    };
  }, [quote]);

  const getPaymentAmount = (type: 'partial' | 'full') => {
    return type === 'partial' ? partialAmount : fullAmount;
  };

  // Create Payment Intent when quote or payment type changes
  useEffect(() => {
    if (!quote || !quote._id) return;

    const createIntent = async () => {
      const amount = getPaymentAmount(paymentType);
      if (isNaN(amount) || amount <= 0) {
        // Don't create intent for zero/invalid amount
        return;
      }

      try {
        const response = await apiService.payments.createPaymentIntent(
          quote._id, 
          paymentType, 
          amount
        );
        setClientSecret(response.data.data.clientSecret);
      } catch (error) {
        toast.error('Erreur lors de l\'initialisation du paiement.');
        setClientSecret(null);
      }
    };

    createIntent();
  }, [quote, paymentType, partialAmount, fullAmount]);

  const handlePaymentSuccess = (id: string) => {
    setPaymentId(id);
    setPaymentSuccess(true);
    toast.success('Paiement effectué avec succès !');
    apiService.payments.confirmPayment(id).catch(err => console.error('Failed to confirm payment on backend', err));
  };

  const handlePaymentError = (errorMsg: string) => {
    toast.error(`Erreur de paiement : ${errorMsg}`);
  };

  if (isLoading) {
    return <Layout><div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div></div></Layout>;
  }

  if (!quote) {
    return <Layout><div className="text-center py-12"><AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" /><h2 className="text-xl font-semibold">Devis non trouvé</h2></div></Layout>;
  }

  if (paymentSuccess) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <Card className="p-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-6" />
            <h2 className="text-2xl font-bold">Paiement réussi !</h2>
            <p className="text-gray-600 mb-6">Votre paiement de {getPaymentAmount(paymentType).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })} a bien été enregistré.</p>
            <p className="text-sm text-gray-500">Référence: {paymentId}</p>
            <div className="mt-8 flex justify-center gap-4">
              <Button onClick={() => navigate('/client/payments')}>Voir mes paiements</Button>
              <Button variant="ghost" onClick={() => navigate('/client/dashboard')}>Tableau de bord</Button>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-8 p-4">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-2" />Retour</Button>
          <div>
            <h1 className="text-3xl font-bold">Paiement Sécurisé</h1>
            <p className="text-gray-600">Devis {quote.reference} - {quote.title}</p>
          </div>
        </div>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4"><FileText className="w-5 h-5 inline mr-2" />Récapitulatif</h3>
          <div className="space-y-2">
            <div className="flex justify-between"><span className="text-gray-600">Sous-total HT:</span><span>{(quote.subtotal || 0).toFixed(2)} €</span></div>
            <div className="flex justify-between"><span className="text-gray-600">TVA:</span><span>{(quote.taxAmount || 0).toFixed(2)} €</span></div>
            <div className="flex justify-between font-bold border-t pt-2 mt-2"><span>Total TTC:</span><span>{(quote.total || 0).toFixed(2)} €</span></div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4"><Percent className="w-5 h-5 inline mr-2" />Type de paiement</h3>
          <div className="space-y-4">
            <div onClick={() => setPaymentType('partial')} className={`flex justify-between items-center p-4 border-2 rounded-lg cursor-pointer ${paymentType === 'partial' ? 'border-primary-500 bg-primary-50' : 'border-gray-200'}`}>
              <div>
                <h4>Acompte 50% HT</h4>
                <p className="text-sm text-gray-600">Pour commencer les travaux</p>
              </div>
              <p className="font-semibold">{partialAmount.toFixed(2)} €</p>
            </div>
            <div onClick={() => setPaymentType('full')} className={`flex justify-between items-center p-4 border-2 rounded-lg cursor-pointer ${paymentType === 'full' ? 'border-primary-500 bg-primary-50' : 'border-gray-200'}`}>
              <div>
                <h4>Paiement intégral</h4>
                <p className="text-sm text-gray-600">Régler la totalité du devis</p>
              </div>
              <p className="font-semibold">{fullAmount.toFixed(2)} €</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          {clientSecret ? (
            <StripePaymentForm
              clientSecret={clientSecret}
              amount={getPaymentAmount(paymentType)}
              description={`Paiement pour devis ${quote.reference}`}
              onPaymentSuccess={handlePaymentSuccess}
              onPaymentError={handlePaymentError}
            />
          ) : (
            <div className="flex justify-center items-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div></div>
          )}
        </Card>

        <div className="text-center text-sm text-gray-500 flex items-center justify-center">
          <Shield className="w-4 h-4 mr-2" />
          Paiement sécurisé par Stripe. Vos données sont protégées.
        </div>
      </div>
    </Layout>
  );
};

export default StripePayment;
